<template>
  <div class="tab-content">
    <!-- Header -->
    <div class="section-header">
      <div>
        <h3>Asociaciones Entidad-Atributo</h3>
        <p class="text-muted">Configure qué atributos están disponibles para cada entidad</p>
      </div>
    </div>

    <!-- Grupos colapsables por entidad -->
    <el-collapse
      v-model="activeEntidades"
      accordion
    >
      <el-collapse-item
        v-for="entidad in entidades"
        :key="entidad.codigo"
        :name="entidad.codigo"
      >
        <template #title>
          <div class="collapse-header">
            <span class="entidad-nombre">{{ entidad.nombre }}</span>
            <el-tag
              size="small"
              class="ml-2"
            >
              {{ getRelacionesPorEntidad(entidad.codigo).length }} atributo(s)
            </el-tag>
          </div>
        </template>

        <!-- Tabla de atributos asociados -->
        <div class="entidad-content">
          <div class="mb-3">
            <el-button
              type="primary"
              size="small"
              @click="$emit('open-relacion-modal', entidad.codigo)"
            >
              <i class="el-icon-plus"></i> Asociar Atributo
            </el-button>
          </div>

          <el-table
            v-loading="loading"
            :data="getRelacionesPorEntidad(entidad.codigo)"
            border
            stripe
            empty-text="No hay atributos asociados a esta entidad"
          >
            <el-table-column
              prop="Atributo.Etiqueta"
              label="Atributo"
              min-width="200"
            />
            <el-table-column
              prop="Atributo.Clave"
              label="Clave"
              width="150"
            />
            <el-table-column
              prop="Atributo.Tipo"
              label="Tipo"
              width="120"
            >
              <template #default="{ row }">
                <el-tag :type="getTipoTagType(row.Atributo?.Tipo)">
                  {{ getTipoLabel(row.Atributo?.Tipo) }}
                </el-tag>
              </template>
            </el-table-column>
            <el-table-column
              label="Requerido"
              width="100"
              align="center"
            >
              <template #default="{ row }">
                <el-switch
                  v-model="row.Requerido"
                  @change="$emit('update-relacion', row)"
                />
              </template>
            </el-table-column>
            <el-table-column
              prop="Orden"
              label="Orden"
              width="140"
            >
              <template #default="{ row }">
                <el-input-number
                  ref="inputRef"
                  v-model="row.Orden"
                  :min="0"
                  :max="999"
                  :formatter="formatter"
                  :parser="parser"
                  size="small"
                  @focus="handleFocus"
                  @blur="handleBlur(row.Orden)"
                  @change="$emit('update-relacion', row)"
                />
              </template>
            </el-table-column>
            <el-table-column
              label="Acciones"
              width="160"
              fixed="right"
            >
              <template #default="{ row }">
                <el-tooltip
                  content="Criterios de visualización"
                  placement="top"
                >
                  <el-button
                    size="small"
                    text
                    :icon="Setting"
                    @click="$emit('open-criterios-modal', row, entidad.nombre)"
                  />
                </el-tooltip>
                <el-popconfirm
                  title="¿Eliminar esta asociación?"
                  confirm-button-text="Sí"
                  cancel-button-text="No"
                  @confirm="$emit('delete-relacion', row.Codigo)"
                >
                  <template #reference>
                    <el-button
                      size="small"
                      text
                      type="danger"
                      :icon="Delete"
                    />
                  </template>
                </el-popconfirm>
              </template>
            </el-table-column>
          </el-table>
        </div>
      </el-collapse-item>
    </el-collapse>
  </div>
</template>

<script setup>
import { ref } from 'vue';
import {
  Delete,
  Setting,
} from '@element-plus/icons-vue';
import { useLocaleNumberInput } from '@/shared/composables/useLocaleNumberInput.js';

const { inputRef, formatter, parser, handleFocus, handleBlur } = useLocaleNumberInput(null);

const props = defineProps({
  entidades: {
    type: Array,
    required: true,
  },
  relaciones: {
    type: Array,
    required: true,
  },
  entidadesBd: {
    type: Array,
    required: true,
  },
  loading: {
    type: Boolean,
    default: false,
  },
});

defineEmits(['open-relacion-modal', 'update-relacion', 'delete-relacion', 'open-criterios-modal']);

const activeEntidades = ref([]);

function getRelacionesPorEntidad(codigoEntidad) {
  const entidadBd = props.entidadesBd.find((e) => e.Nombre === codigoEntidad);
  if (!entidadBd) return [];
  return props.relaciones.filter((r) => r.CodigoEntidad === entidadBd.Codigo);
}

function getTipoTagType(tipo) {
  const types = {
    string: '',
    number: 'success',
    boolean: 'info',
    date: 'warning',
    select: 'primary',
    multiselect: 'danger',
  };
  return types[tipo] || '';
}

function getTipoLabel(tipo) {
  const labels = {
    string: 'Texto',
    number: 'Número',
    boolean: 'Booleano',
    date: 'Fecha',
    select: 'Select',
    multiselect: 'Multi-Select',
  };
  return labels[tipo] || tipo;
}
</script>

<style scoped>
.tab-content {
  padding: 20px;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}

.section-header h3 {
  margin: 0 0 5px 0;
  font-size: 18px;
  font-weight: 600;
}

.text-muted {
  color: #909399;
  font-size: 14px;
}

.collapse-header {
  display: flex;
  align-items: center;
  width: 100%;
}

.entidad-nombre {
  font-weight: 600;
  font-size: 15px;
}

.ml-2 {
  margin-left: 8px;
}

.mb-3 {
  margin-bottom: 12px;
}

.entidad-content {
  padding: 12px 0;
}
</style>
